/* Scentedelic lock poster: liquid light (WebGL), letter reveal, password dialog.
   The liquid is a domain-warped noise field drawn by one fragment shader at a
   reduced resolution and scaled up (it is soft by nature, so this costs nothing
   visually). The pointer stirs it: a swirl and a ripple around the cursor, with
   a warm glow. With no pointer, an invisible stirrer drifts on its own. */
(function () {
  function hex(h) {
    h = String(h || '').replace('#', '').trim();
    if (h.length === 3) { h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; }
    if (h.length !== 6) { return [0, 0, 0]; }
    return [parseInt(h.substr(0, 2), 16) / 255, parseInt(h.substr(2, 2), 16) / 255, parseInt(h.substr(4, 2), 16) / 255];
  }

  // Split the heading into letters (kept whole per word so lines never break
  // mid-word) for the staggered blur-in.
  function letters(el) {
    if (!el || el.getAttribute('data-split')) { return; }
    el.setAttribute('data-split', '1');
    el.setAttribute('aria-label', el.textContent.trim());
    var words = el.textContent.trim().split(' ').filter(Boolean);
    var i = 0;
    el.textContent = '';
    words.forEach(function (w, wi) {
      var ws = document.createElement('span');
      ws.className = 'sd-lock__word';
      ws.setAttribute('aria-hidden', 'true');
      for (var c = 0; c < w.length; c++) {
        var cs = document.createElement('span');
        cs.className = 'sd-lock__char';
        cs.style.setProperty('--i', i++);
        cs.textContent = w[c];
        ws.appendChild(cs);
      }
      el.appendChild(ws);
      if (wi < words.length - 1) { el.appendChild(document.createTextNode(' ')); i++; }
    });
  }

  var VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.0,1.0);}';

  var FRAG = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'uniform vec2 u_res;',
    'uniform float u_time;',
    'uniform vec2 u_mouse;',
    'uniform float u_stir;',
    'uniform vec2 u_focus;',
    'uniform float u_warp;',
    'uniform float u_bands;',
    'uniform float u_bandmix;',
    'uniform vec3 u_c0;',
    'uniform vec3 u_c1;',
    'uniform vec3 u_c2;',
    'uniform vec3 u_c3;',
    'float hash(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}',
    'float noise(vec2 p){vec2 i=floor(p);vec2 f=fract(p);vec2 u=f*f*(3.0-2.0*f);',
    '  return mix(mix(hash(i),hash(i+vec2(1.0,0.0)),u.x),mix(hash(i+vec2(0.0,1.0)),hash(i+vec2(1.0,1.0)),u.x),u.y);}',
    'float fbm(vec2 p){float v=0.0;float a=0.5;mat2 r=mat2(0.8,0.6,-0.6,0.8);',
    '  for(int i=0;i<5;i++){v+=a*noise(p);p=r*p*2.02+vec2(1.7,9.2);a*=0.5;}return v;}',
    'void main(){',
    '  vec2 uv=(gl_FragCoord.xy-0.5*u_res)/u_res.y;',
    '  vec2 m=(u_mouse-0.5*u_res)/u_res.y;',
    '  vec2 d=uv-m;float r=length(d);',
    '  float sw=u_stir*2.4*exp(-r*r*7.0);',
    '  float cs=cos(sw);float sn=sin(sw);',
    '  vec2 w=m+mat2(cs,-sn,sn,cs)*d;',
    '  w+=u_stir*0.035*(d/(r+0.0001))*sin(r*30.0-u_time*2.6)*exp(-r*4.0);',
    '  float t=u_time*0.055;',
    '  vec2 p=w*1.45;',
    '  vec2 q=vec2(fbm(p+vec2(0.0,t)),fbm(p+vec2(5.2,1.3)-t));',
    '  vec2 s=vec2(fbm(p+u_warp*q+vec2(1.7,9.2)+0.8*t),fbm(p+u_warp*q+vec2(8.3,2.8)-0.6*t));',
    '  float f=fbm(p+u_warp*s);',
    '  vec3 col=mix(u_c0,u_c1,smoothstep(0.18,0.85,f));',
    '  col=mix(col,u_c2,smoothstep(0.55,1.15,length(q)*f*1.6)*0.85);',
    '  float b=0.5+0.5*sin((f*1.3+s.x)*u_bands*6.2831+u_time*0.35);',
    '  float edge=smoothstep(0.7,1.0,b)*smoothstep(0.25,0.6,f);',
    '  vec3 sheen=mix(u_c2,u_c3,smoothstep(0.35,0.75,s.y));',
    '  col+=edge*sheen*u_bandmix;',
    '  col+=u_stir*u_c3*0.22*exp(-r*r*14.0);',
    '  float v=smoothstep(1.55,0.15,length(uv-u_focus));',
    '  col*=0.25+0.75*v;',
    '  gl_FragColor=vec4(col,1.0);',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      if (window.console) { console.error('sd-lock shader:', gl.getShaderInfoLog(sh)); }
      return null;
    }
    return sh;
  }

  function liquid(root, still) {
    var canvas = root.querySelector('.sd-lock__canvas');
    if (!canvas) { return; }
    var gl = null;
    try { gl = canvas.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false, preserveDrawingBuffer: true }); } catch (e) { gl = null; }
    if (!gl) { root.classList.add('is-nogl'); return; }

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) { root.classList.add('is-nogl'); return; }
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { root.classList.add('is-nogl'); return; }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var U = {};
    ['u_res', 'u_time', 'u_mouse', 'u_stir', 'u_focus', 'u_warp', 'u_bands', 'u_bandmix', 'u_c0', 'u_c1', 'u_c2', 'u_c3'].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });

    var d = root.dataset;
    var speed = (parseFloat(d.speed) || 6) / 6;
    var res = Math.max(0.3, Math.min(1, (parseFloat(d.res) || 60) / 100));
    var stirMax = Math.max(0, (parseFloat(d.stir) || 0) / 100);
    gl.uniform1f(U.u_warp, (parseFloat(d.warp) || 40) / 10);
    gl.uniform1f(U.u_bands, parseFloat(d.bands) || 0);
    gl.uniform1f(U.u_bandmix, (parseFloat(d.bandmix) || 0) / 100);
    gl.uniform3fv(U.u_c0, hex(d.c0));
    gl.uniform3fv(U.u_c1, hex(d.c1));
    gl.uniform3fv(U.u_c2, hex(d.c2));
    gl.uniform3fv(U.u_c3, hex(d.c3));

    var W = 0, H = 0, wide = false, scale = 1;
    var mx = 0, my = 0, tx = 0, ty = 0, stir = 0, stirTarget = 0;
    var lastMove = -1e9, lastPX = null, lastPY = null, lastPT = 0, vel = 0;
    var raf = null, onScreen = true, t0 = null;

    function layout() {
      var cw = canvas.clientWidth, ch = canvas.clientHeight;
      if (!cw || !ch) { return false; }
      wide = cw >= 900 && cw / ch >= 1.2;
      scale = res;
      var nw = Math.max(1, Math.round(cw * scale)), nh = Math.max(1, Math.round(ch * scale));
      if (canvas.width !== nw || canvas.height !== nh) {
        canvas.width = nw; canvas.height = nh;
        gl.viewport(0, 0, nw, nh);
      }
      W = nw; H = nh;
      // Focus: where the light gathers. Right of centre on a landscape screen.
      gl.uniform2f(U.u_focus, wide ? 0.18 * (W / H) : 0.0, 0.0);
      return true;
    }

    function draw(t) {
      if (!layout()) { return; }
      // The stirrer: the pointer while it is moving, otherwise a slow figure-eight
      // around the focus so the liquid is never quite still.
      var idle = t - lastMove > 2200;
      if (idle) {
        var fx = wide ? 0.66 : 0.5;
        tx = W * (fx + 0.16 * Math.sin(t * 0.00021));
        ty = H * (0.5 + 0.2 * Math.sin(t * 0.00034));
        stirTarget = 0.35 * stirMax;
      } else {
        stirTarget = Math.min(1.6, 0.55 + vel * 0.9) * stirMax;
      }
      mx += (tx - mx) * 0.07;
      my += (ty - my) * 0.07;
      stir += (stirTarget - stir) * 0.05;
      vel *= 0.94;

      gl.uniform2f(U.u_res, W, H);
      gl.uniform1f(U.u_time, (t / 1000) * speed);
      gl.uniform2f(U.u_mouse, mx, my);
      gl.uniform1f(U.u_stir, stir);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    function frame(now) {
      raf = null;
      if (t0 === null) {
        t0 = now || 0;
        if (layout()) { mx = tx = W * (wide ? 0.66 : 0.5); my = ty = H * 0.5; }
      }
      draw((now || 0) - t0 + 20000);
      if (!still && onScreen && !document.hidden) { raf = requestAnimationFrame(frame); }
    }
    function kick() { if (raf === null) { raf = requestAnimationFrame(frame); } }

    // Test hook: render one frame at a fixed time and pointer (used for previews).
    root.sdLockRender = function (t, px, py, s) {
      layout();
      mx = tx = px * W; my = ty = (1 - py) * H; stir = stirTarget = s * stirMax; vel = 0;
      lastMove = t; draw(t);
    };

    window.addEventListener('pointermove', function (e) {
      var b = canvas.getBoundingClientRect();
      if (!b.width) { return; }
      var now = performance.now() - (t0 || 0) + 20000;
      var px = (e.clientX - b.left) / b.width, py = (e.clientY - b.top) / b.height;
      if (lastPX !== null) {
        var dt = Math.max(8, now - lastPT);
        vel = Math.min(2.5, vel + Math.hypot(px - lastPX, py - lastPY) / dt * 60);
      }
      lastPX = px; lastPY = py; lastPT = now;
      tx = px * W; ty = (1 - py) * H;
      lastMove = now;
      kick();
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { onScreen = es[0].isIntersecting; if (onScreen) { kick(); } }, { threshold: 0 }).observe(root);
    }
    document.addEventListener('visibilitychange', function () { if (!document.hidden) { kick(); } });
    window.addEventListener('resize', kick, { passive: true });
    kick();
  }

  function init(root) {
    if (root.getAttribute('data-sd-lock-ready')) { return; }
    root.setAttribute('data-sd-lock-ready', '1');

    var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var still = mq ? mq.matches : false;

    // Password dialog
    var dlg = root.querySelector('[data-sd-lock-dialog]');
    var openBtn = root.querySelector('[data-sd-lock-open]');
    if (!dlg && openBtn) { openBtn.style.display = 'none'; }
    if (dlg && openBtn && typeof dlg.showModal === 'function') {
      openBtn.addEventListener('click', function () { dlg.showModal(); });
      var closeBtn = dlg.querySelector('[data-sd-lock-close]');
      if (closeBtn) { closeBtn.addEventListener('click', function () { dlg.close(); }); }
      if (dlg.hasAttribute('data-open-now')) { dlg.showModal(); }
    }

    // Entrance. The section renders with .is-armed (content held back);
    // after a form post or for reduced motion it shows at once instead.
    var posted = /customer_posted|contact_posted|form_type=customer/.test(location.href + location.hash);
    if (still || posted) {
      root.classList.add('is-instant', 'is-live');
    } else {
      letters(root.querySelector('[data-sd-letters]'));
      requestAnimationFrame(function () { requestAnimationFrame(function () { root.classList.add('is-live'); }); });
    }

    liquid(root, still);
  }

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sd-lock]');
    for (var i = 0; i < nodes.length; i++) { init(nodes[i]); }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();